/**
 * Notes on the card: lay a zone's text out inside a shape, line by line.
 *
 * The layout is computed here rather than left to the browser, because the
 * text has to fit a shape (the zone's own outline - by default since 2026-10-17,
 * the owner: "always, the note by default follows the shape, I don't want to see
 * a square in a diamond shape" - or an upright box, or the diamond of brief
 * s.11, when chosen) that CSS cannot flow text into, and because the preview and
 * the printed card must break lines in exactly the same places. Measuring is
 * injected (`Measure`), so the same code runs against a canvas in the browser
 * and in the print renderer. Shaping and right-to-left ordering within a line
 * are left to the renderer, which does them correctly for every script.
 *
 * All coordinates are millimetres in the zone's own UPRIGHT frame: x to the
 * right and y down as the text will be read on the folded card, origin at the
 * top-left of the zone's upright bounding box. `toPage` maps that frame onto
 * the printed page.
 *
 * A note can be turned (`rotationDeg`, clockwise, 2026-10-10). The text is then
 * laid out in a frame turned by that much around the zone's middle - the zone
 * as seen from that frame - so turned text still fits inside the zone; `toPage`
 * includes the turn.
 */
import { compose } from './affine.ts';
import { zoneFrame } from './placement.ts';
import type { Affine, Pt, ZoneText } from './types.ts';

export const RTL_LANGUAGES = new Set(['ar', 'he', 'fa', 'ur', 'ps', 'sd', 'ug', 'yi', 'dv', 'ckb']);
export const isRtl = (language: string) => RTL_LANGUAGES.has(language.split('-')[0].toLowerCase());

/** The fonts a note can be set in. Every one is bundled, so print and preview match. */
export const FONTS = {
  sans: { label: 'Clean', css: "'Noto Sans', 'Noto Naskh Arabic', 'Noto Sans Hebrew', sans-serif" },
  serif: { label: 'Classic', css: "'Noto Serif', 'Noto Naskh Arabic', 'Noto Sans Hebrew', serif" },
  hand: { label: 'Handwritten', css: "'Caveat', 'Noto Naskh Arabic', 'Noto Sans Hebrew', cursive" },
} as const;
export type FontKey = keyof typeof FONTS;

/** The shapes a note can be fitted into, the default first. */
export const TEXT_SHAPES = ['zone', 'square', 'diamond'] as const;

export const DEFAULT_TEXT = {
  shape: 'zone',
  sizeMm: 6,
  color: '#1b2430',
  bold: false,
  italic: false,
  align: 'center' as const,
  font: 'sans' as FontKey,
  backdrop: 0.72,
};
export const MIN_TEXT_MM = 2;

export interface TextStyle { font: string; sizeMm: number; bold: boolean; italic: boolean }
/** Width, in mm, of `text` set in `style`. */
export type Measure = (text: string, style: TextStyle) => number;

export interface LaidLine { text: string; x: number; y: number; anchor: 'start' | 'middle' | 'end' }
export interface TextLayout {
  lines: LaidLine[];
  sizeMm: number;
  /** The shape the text was fitted into, upright frame - also where the veil goes. */
  shape: Pt[];
  /** Upright frame -> printed page. */
  toPage: Affine;
  rtl: boolean;
  style: TextStyle;
  color: string;
  /** True when even the smallest size could not fit everything. */
  overflow: boolean;
  /**
   * Where an invitation's RSVP code goes, upright frame (2026-10-21): a square at the foot of
   * the note, the words fitted above it. Null when one was asked for and does not fit.
   */
  code?: CodeSpot | null;
}

/** An RSVP code's square and the line under it ("Scan to RSVP"), upright frame, mm; `quiet`: the room for a white border round the square. */
export interface CodeSpot { x: number; y: number; size: number; quiet: number; labelX: number; labelY: number; labelSize: number }
/** Asked of layoutText for an invitation: the code's side and the label's size, mm. */
export interface CodeRequest { sizeMm: number; labelMm: number }

const dot = (p: Pt, q: Pt) => p[0] * q[0] + p[1] * q[1];

/** The zone's outline in its own upright frame, and the map back to the page. */
export function uprightZone(outline: Pt[], up: Pt) {
  const f = zoneFrame([outline], up);
  const r0 = Math.min(...outline.map((p) => dot(p, f.right)));
  const d0 = Math.min(...outline.map((p) => dot(p, f.down)));
  const polygon = outline.map((p): Pt => [dot(p, f.right) - r0, dot(p, f.down) - d0]);
  const tl: Pt = [f.right[0] * r0 + f.down[0] * d0, f.right[1] * r0 + f.down[1] * d0];
  const toPage: Affine = [f.right[0], f.right[1], f.down[0], f.down[1], tl[0], tl[1]];
  return { polygon, toPage, width: f.width, height: f.height };
}

/** Clip a polygon to a convex one (Sutherland-Hodgman). */
function clipConvex(poly: Pt[], convex: Pt[]): Pt[] {
  let out = poly;
  const area2 = convex.reduce((s, a, i) => { const b = convex[(i + 1) % convex.length]; return s + a[0] * b[1] - b[0] * a[1]; }, 0);
  const sign = Math.sign(area2) || 1;
  for (let i = 0; i < convex.length && out.length; i++) {
    const a = convex[i], b = convex[(i + 1) % convex.length];
    const side = (p: Pt) => sign * ((b[0] - a[0]) * (p[1] - a[1]) - (b[1] - a[1]) * (p[0] - a[0]));
    const next: Pt[] = [];
    out.forEach((p, j) => {
      const q = out[(j + 1) % out.length];
      const sp = side(p), sq = side(q);
      if (sp >= 0) next.push(p);
      if ((sp >= 0) !== (sq >= 0)) {
        const t = sp / (sp - sq);
        next.push([p[0] + (q[0] - p[0]) * t, p[1] + (q[1] - p[1]) * t]);
      }
    });
    out = next;
  }
  return out;
}

function inside(poly: Pt[], [x, y]: Pt): boolean {
  let hit = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const [xi, yi] = poly[i], [xj, yj] = poly[j];
    if ((yi > y) !== (yj > y) && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) hit = !hit;
  }
  return hit;
}

/**
 * The largest upright box in the proportions of the zone's upright bounds
 * that fits inside the zone. Its middle is searched for on a grid, because in
 * a triangle (most zones) the best box sits towards the right angle, not at
 * the middle.
 */
function boxIn(polygon: Pt[], width: number, height: number): Pt[] | null {
  const corners = (cx: number, cy: number, k: number): Pt[] => {
    const hw = (width * k) / 2, hh = (height * k) / 2;
    return [[cx - hw, cy - hh], [cx + hw, cy - hh], [cx + hw, cy + hh], [cx - hw, cy + hh]];
  };
  const fits = (cx: number, cy: number, k: number) => corners(cx, cy, k).every((p) => inside(polygon, p));
  let best: { cx: number; cy: number; k: number } | null = null;
  const N = 12;
  for (let i = 1; i < N; i++) {
    for (let j = 1; j < N; j++) {
      const cx = (width * i) / N, cy = (height * j) / N;
      if (!inside(polygon, [cx, cy])) continue;
      let lo = 0, hi = 1;
      for (let step = 0; step < 24; step++) {
        const mid = (lo + hi) / 2;
        if (fits(cx, cy, mid)) lo = mid; else hi = mid;
      }
      if (!best || lo > best.k) best = { cx, cy, k: lo };
    }
  }
  return best && best.k > 0.05 ? corners(best.cx, best.cy, best.k * 0.999) : null;
}

/**
 * The shape a note's text is fitted into: the frame's own, unless the person
 * chose a box or a diamond (ZoneText.shapeChosen).
 */
export const noteShape = (note: { shape?: string; shapeChosen?: boolean }) =>
  note.shapeChosen && note.shape ? note.shape : 'zone';

/** The shape text is fitted into: the upright box ('square'), the diamond inscribed in the zone, or the zone itself. */
export function textShape(polygon: Pt[], width: number, height: number, shape: string): Pt[] {
  if (shape === 'square') return boxIn(polygon, width, height) ?? polygon;
  if (shape !== 'diamond') return polygon;
  const diamond: Pt[] = [[width / 2, 0], [width, height / 2], [width / 2, height], [0, height / 2]];
  const clipped = clipConvex(diamond, polygon);
  return clipped.length >= 3 ? clipped : polygon;
}

/**
 * The zone seen from a frame turned `deg` clockwise about its middle: its
 * outline in that frame (from 0,0), the frame's size, and the map from that
 * frame back to the upright one.
 */
function turnedFrame(polygon: Pt[], width: number, height: number, deg: number) {
  const a = (deg * Math.PI) / 180, cos = Math.cos(a), sin = Math.sin(a);
  const cx = width / 2, cy = height / 2;
  // upright -> turned: undo the turn about the middle
  const back = polygon.map(([x, y]): Pt => [cos * (x - cx) + sin * (y - cy), -sin * (x - cx) + cos * (y - cy)]);
  const x0 = Math.min(...back.map((p) => p[0])), y0 = Math.min(...back.map((p) => p[1]));
  const x1 = Math.max(...back.map((p) => p[0])), y1 = Math.max(...back.map((p) => p[1]));
  // turned -> upright: shift by (x0, y0), turn, put the middle back
  const toUpright: [number, number, number, number, number, number] =
    [cos, sin, -sin, cos, cx + cos * x0 - sin * y0, cy + sin * x0 + cos * y0];
  return { polygon: back.map(([x, y]): Pt => [x - x0, y - y0]), width: x1 - x0, height: y1 - y0, toUpright };
}

/** Where the horizontal line at height y crosses the shape: [left, right], or null. */
function chord(poly: Pt[], y: number): [number, number] | null {
  const xs: number[] = [];
  poly.forEach((a, i) => {
    const b = poly[(i + 1) % poly.length];
    if ((a[1] <= y && b[1] >= y) || (b[1] <= y && a[1] >= y)) {
      if (Math.abs(b[1] - a[1]) < 1e-9) xs.push(a[0], b[0]);
      else xs.push(a[0] + ((y - a[1]) * (b[0] - a[0])) / (b[1] - a[1]));
    }
  });
  return xs.length >= 2 ? [Math.min(...xs), Math.max(...xs)] : null;
}

const graphemes = (s: string): string[] => {
  const Seg = (Intl as unknown as { Segmenter?: new (l?: string, o?: object) => { segment(s: string): Iterable<{ segment: string }> } }).Segmenter;
  return Seg ? [...new Seg(undefined, { granularity: 'grapheme' }).segment(s)].map((g) => g.segment) : [...s];
};

/**
 * Lay the lines out top to bottom from `top`, each in the room the shape has
 * across that line's own height. Returns null when the words do not fit - or,
 * unless `breakWords`, when a word would have to be broken between letters.
 */
function flow(paragraphs: string[], shape: Pt[], top: number, size: number, style: TextStyle,
  measure: Measure, pad: number, breakWords = true): { text: string; lo: number; hi: number; y: number }[] | null {
  const lh = size * 1.3;
  const out: { text: string; lo: number; hi: number; y: number }[] = [];
  let y = top;
  const room = (): [number, number] | null => {
    const a = chord(shape, y + lh * 0.12), b = chord(shape, y + lh * 0.88);
    if (!a || !b) return null;
    const lo = Math.max(a[0], b[0]) + pad, hi = Math.min(a[1], b[1]) - pad;
    return hi - lo > size ? [lo, hi] : null;
  };
  const width = (s: string) => measure(s, { ...style, sizeMm: size });
  for (const para of paragraphs) {
    const words = para.split(/\s+/).filter(Boolean);
    if (!words.length) { y += lh; continue; }
    let i = 0;
    while (i < words.length) {
      const r = room();
      if (!r) return null;
      const max = r[1] - r[0];
      let line = '';
      while (i < words.length) {
        const next = line ? `${line} ${words[i]}` : words[i];
        if (width(next) <= max) { line = next; i++; continue; }
        if (!line) {
          // one word wider than the line: break it between characters
          if (!breakWords) return null;
          const g = graphemes(words[i]);
          let k = 1;
          while (k < g.length && width(g.slice(0, k + 1).join('')) <= max) k++;
          line = g.slice(0, k).join('');
          words[i] = g.slice(k).join('');
          if (!words[i]) i++;
        }
        break;
      }
      out.push({ text: line, lo: r[0], hi: r[1], y });
      y += lh;
    }
  }
  return out;
}

/** Where a line across the shape crosses it: along x at height `v` (axis 'x'), or along y at `v` ('y'). */
function span(shape: Pt[], v: number, axis: 'x' | 'y' = 'x'): [number, number] | null {
  const [a, b] = axis === 'x' ? [1, 0] : [0, 1];   // a: the coordinate held at v; b: the one measured
  let lo = Infinity, hi = -Infinity;
  for (let i = 0; i < shape.length; i++) {
    const p = shape[i], q = shape[(i + 1) % shape.length];
    if ((p[a] - v) * (q[a] - v) > 0 || p[a] === q[a]) continue;
    const t = p[b] + ((v - p[a]) / (q[a] - p[a])) * (q[b] - p[b]);
    lo = Math.min(lo, t); hi = Math.max(hi, t);
  }
  return lo <= hi ? [lo, hi] : null;
}

/**
 * Where an RSVP code and its label go in the shape, with room left for words: at the foot,
 * or - in a shape more than twice as wide as it is tall (the classic card's band) - at the
 * right-hand end, so the words keep the whole height. Returns the square and the line the
 * words must keep to (`cut`: below x = at, or above y = at). Null when the shape is too small.
 */
export function codeSpot(shape: Pt[], req: CodeRequest): { spot: CodeSpot; cut: { axis: 'x' | 'y'; at: number } } | null {
  const xs = shape.map((p) => p[0]), ys = shape.map((p) => p[1]);
  const [x0, x1, y0, y1] = [Math.min(...xs), Math.max(...xs), Math.min(...ys), Math.max(...ys)];
  const pad = 1.5, gap = 0.8, labelH = req.labelMm * 1.25;
  const blockW = req.sizeMm, blockH = req.sizeMm + gap + labelH;
  const minWords = 12;   // at least this much left for the message itself
  const spotAt = (left: number, top: number): CodeSpot => ({
    x: left, y: top, size: req.sizeMm, quiet: pad, labelX: left + req.sizeMm / 2, labelY: top + req.sizeMm + gap + req.labelMm, labelSize: req.labelMm,
  });
  /** The room across the block's whole length: the overlap of the shape's spans along it. */
  const room = (from: number, length: number, axis: 'x' | 'y') => {
    let lo = -Infinity, hi = Infinity;
    for (let k = 0; k <= 6; k++) {
      const s = span(shape, from + (length * k) / 6, axis);
      if (!s) return null;
      lo = Math.max(lo, s[0]); hi = Math.min(hi, s[1]);
    }
    return hi > lo ? [lo, hi] as const : null;
  };
  if (x1 - x0 > 2 * (y1 - y0)) {
    for (let right = x1 - pad; right - blockW >= x0 + minWords; right -= 0.5) {
      const left = right - blockW;
      // the white border too: a pointed end narrows just past the square
      const r = room(left - pad, blockW + 2 * pad, 'y');
      if (!r || r[1] - r[0] < blockH + 2 * pad) continue;
      return { spot: spotAt(left, (r[0] + r[1]) / 2 - blockH / 2), cut: { axis: 'x', at: left - 2 } };
    }
  }
  for (let bottom = y1 - pad; bottom - blockH >= y0 + minWords; bottom -= 0.5) {
    const top = bottom - blockH;
    const r = room(top - pad, blockH + pad, 'x');
    if (!r || r[1] - r[0] < blockW + 2 * pad) continue;
    return { spot: spotAt((r[0] + r[1]) / 2 - blockW / 2, top), cut: { axis: 'y', at: top - 1.5 } };
  }
  return null;
}

/**
 * Fit a zone's note into its shape: as large as the chosen size allows, down
 * to MIN_TEXT_MM, centred vertically in the shape. `opts.code`: an invitation's
 * RSVP code goes at the foot of the shape, and the words above it.
 */
export function layoutText(note: ZoneText, zone: { outline: Pt[]; up: Pt }, measure: Measure, opts: { code?: CodeRequest } = {}): TextLayout {
  const upright = uprightZone(zone.outline, zone.up);
  let { polygon, toPage, width, height } = upright;
  const turn = ((((note.rotationDeg ?? 0) + 180) % 360) + 360) % 360 - 180;
  if (Math.abs(turn) > 1e-9) {
    const f = turnedFrame(polygon, width, height, turn);
    ({ polygon, width, height } = f);
    toPage = compose(upright.toPage, f.toUpright);
  }
  let shape = textShape(polygon, width, height, noteShape(note));
  let code: CodeSpot | null | undefined;
  if (opts.code) {
    const found = codeSpot(shape, opts.code);
    code = found?.spot ?? null;
    if (found) {
      const at = found.cut.at;
      const keep: Pt[] = found.cut.axis === 'y'
        ? [[-1e4, -1e4], [1e4, -1e4], [1e4, at], [-1e4, at]]
        : [[-1e4, -1e4], [at, -1e4], [at, 1e4], [-1e4, 1e4]];
      const cut = clipConvex(shape, keep);
      if (cut.length >= 3) shape = cut;
    }
  }
  const rtl = isRtl(note.language);
  const style: TextStyle = {
    font: FONTS[(note.font as FontKey) in FONTS ? (note.font as FontKey) : 'sans'].css,
    sizeMm: note.sizeMm ?? DEFAULT_TEXT.sizeMm,
    bold: !!note.bold,
    italic: !!note.italic,
  };
  const paragraphs = note.content.replace(/\r/g, '').split('\n');
  const ys = shape.map((p) => p[1]);
  const [y0, y1] = [Math.min(...ys), Math.max(...ys)];
  const align = note.align ?? DEFAULT_TEXT.align;

  let size = style.sizeMm;
  let laid: ReturnType<typeof flow> = null;
  // whole words first, as large as they fit; only if no size down to the
  // smallest keeps them whole is a word broken between letters (2026-10-17: a
  // narrow triangle broke "Today" into "T" and "oday" at the largest size)
  for (const breakWords of [false, true]) {
    for (size = style.sizeMm; size >= MIN_TEXT_MM; size *= 0.92) {
      const lh = size * 1.3;
      const pad = Math.max(1.2, size * 0.3);
      // centre the block: lay out, measure its height, re-centre, lay out again
      let top = (y0 + y1) / 2 - lh / 2;
      for (let pass = 0; pass < 4; pass++) {
        const attempt = flow(paragraphs, shape, top, size, style, measure, pad, breakWords);
        if (!attempt) { laid = null; break; }
        const h = attempt.length * lh;
        const want = Math.max(y0, Math.min((y0 + y1) / 2 - h / 2, y1 - h));
        laid = attempt;
        if (Math.abs(want - top) < 0.05) break;
        top = want;
      }
      if (laid && laid.length && laid[laid.length - 1].y + lh <= y1 + 1e-6) break;
      laid = null;
    }
    if (laid) break;
  }
  const overflow = !laid;
  if (!laid) {
    size = MIN_TEXT_MM;
    laid = flow(paragraphs, shape, y0, size, style, measure, 1) || [];
  }

  // The renderer sets `direction` from the language, and then SVG's
  // text-anchor 'start' is the reading start: the left edge of a
  // left-to-right line, the right edge of a right-to-left one.
  const lines = laid.map((l): LaidLine => {
    const baseline = l.y + size * 1.3 * 0.5 + size * 0.34;
    if (align === 'center') return { text: l.text, x: (l.lo + l.hi) / 2, y: baseline, anchor: 'middle' };
    if (align === 'start') return { text: l.text, x: rtl ? l.hi : l.lo, y: baseline, anchor: 'start' };
    return { text: l.text, x: rtl ? l.lo : l.hi, y: baseline, anchor: 'end' };
  });
  return { lines, sizeMm: size, shape, toPage, rtl, style: { ...style, sizeMm: size }, color: note.color ?? DEFAULT_TEXT.color, overflow,
    ...(opts.code ? { code } : {}) };
}
